﻿import clsx from 'clsx';
import * as CSS from 'csstype';
import {
  type FormEvent,
  forwardRef,
  Fragment,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { ChevronLeftIcon, ChevronRightIcon } from '@dynatrace/strato-icons';

import { ToggleButtonGroupContext } from './contexts/ToggleButtonGroupContext.js';
import { Item } from './Item.js';
import { Prefix } from './Prefix.js';
import * as styles from './styles.sty.js';
import { Suffix } from './Suffix.js';
import { Button } from '../../buttons/button/Button.js';
import { useContainerSize } from '../../core/hooks/useContainerSize.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useId } from '../../core/hooks/useId.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import { useVisualRefreshEnabled } from '../../core/hooks/useVisualRefreshEnabled.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { DOMProps } from '../../core/types/dom.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { Divider } from '../../layouts/divider/Divider.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { getI18nValidationState } from '../forms-core/hooks/useFormFieldValidation.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import type { FormControlRef } from '../shared-types.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';

const COMPONENT_NAME = 'ToggleButtonGroup';

//#region typings
/**
 * Accepted properties for ToggleButtonGroup
 * @public
 */
export type ToggleButtonGroupProps = FormControlProps<
  string,
  (value: string, event?: FormEvent<HTMLElement | SVGElement>) => void | never
> &
  PropsWithChildren &
  DOMProps &
  StylingProps &
  DataTestId &
  MaskingProps &
  BehaviorTrackingProps & {
    /**
     * The width of the ToggleButtonGroup.
     * @defaultValue 'content'
     */
    width?: 'content' | 'full' | CSS.Property.Width<string & {}>;
  };
//#endregion

const ToggleButtonGroupRoot = forwardRef<
  FormControlRef<HTMLDivElement> | HTMLDivElement | null,
  ToggleButtonGroupProps
>((props, forwardedRef) => {
  const fallbackName = useId();
  const {
    children,
    id: propId,
    disabled: propDisabled,
    required: propRequired,
    readOnly: propReadOnly,
    value,
    defaultValue,
    name = fallbackName,
    width = 'content',
    onChange,
    onValidityChange,
    className: consumerClassName,
    style: consumerStyle,
    'aria-disabled': propAriaDisabled,
    'data-dtrum-allow': dataDtrumAllow,
    'data-dtrum-mask': dataDtrumMask,
    ...remainingProps
  }: ToggleButtonGroupProps = props;
  const intl = useIntl();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const _emitValidationState = useValidationStateEmitter(onValidityChange);
  const emitValidationState = useCustomMessageEmitter(
    _emitValidationState,
    () =>
      getI18nValidationState(
        intl,
        wrapperRef.current?.querySelector('input'),
        'manual',
      ),
  );

  const [isOverflowingLeft, setIsOverflowingLeft] = useState(false);
  const [isOverflowingRight, setIsOverflowingRight] = useState(false);

  const {
    disabled: formControlDisabled,
    ariaDisabled,
    required: isRequired,
    id: formControlId,
    readOnly: isReadOnly,
  } = useSyncedFormControlProps({
    propDisabled,
    propRequired,
    propId,
    propAriaDisabled,
    propReadOnly,
  });

  const isDisabled = propDisabled ?? formControlDisabled;
  const isDisabledOrAriaDisabled = isDisabled || ariaDisabled;

  const [selectedValue, setSelectedValue] = useControllableState({
    defaultValue,
    value,
  });

  const { dispatch: messagesContextDispatch, state: messageContextState } =
    useFormFieldMessagesContext();

  const hasError = messageContextState.showError;

  useImperativeHandle(
    forwardedRef,
    (): FormControlRef<HTMLDivElement> | HTMLDivElement | null => {
      const newRef = {
        element: wrapperRef.current,
        focus() {
          const checkedInput = wrapperRef.current?.querySelector(
            'input:checked',
          ) as HTMLInputElement;
          (
            checkedInput ??
            (wrapperRef.current?.querySelector('input') as HTMLInputElement)
          )?.focus();
        },
        validate() {
          wrapperRef.current
            ?.querySelectorAll('input')
            ?.forEach((i) => i.checkValidity());
          messagesContextDispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
          const firstInput = wrapperRef.current?.querySelector(
            'input',
          ) as HTMLInputElement | null;
          emitValidationState(
            getI18nValidationState(intl, firstInput, 'manual'),
          );
        },
        // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
        validateAsync: async () => {
          wrapperRef.current
            ?.querySelectorAll('input')
            ?.forEach((i) => i.checkValidity());
          messagesContextDispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
          const firstInput = wrapperRef.current?.querySelector(
            'input',
          ) as HTMLInputElement | null;
          return emitValidationState(
            getI18nValidationState(intl, firstInput, 'manual'),
          );
        },
      };

      if (!wrapperRef.current) {
        return null;
      }

      return new Proxy<HTMLDivElement>(wrapperRef.current, {
        get: function <
          Property extends keyof HTMLDivElement &
            'element' &
            'focus' &
            'validate' &
            'validateAsync',
        >(target: HTMLDivElement, property: Property) {
          if (property in newRef) {
            return newRef[property];
          }
          return target[property];
        },
      });
    },
    [messagesContextDispatch, emitValidationState],
  );

  const handleChange = useCallback(
    (value: string, event?: FormEvent<HTMLElement | SVGElement>) => {
      setSelectedValue?.(value);
      onChange?.(value, event);
    },
    [onChange, setSelectedValue],
  );

  const customWidth =
    width !== 'content' && width !== 'full' ? width : undefined;

  const [slots] = useSlots(children, {
    items: { match: Item, multiple: true },
    prefix: Prefix,
    suffix: Suffix,
  });

  const valueProps = useMemo(
    () => ({
      name,
      isDisabled,
      ariaDisabled,
      isReadOnly,
      isRequired,
      hasError,
      selectedValue,
      onChange: handleChange,
      onValidityChange: emitValidationState,
    }),
    [
      name,
      isDisabled,
      ariaDisabled,
      isReadOnly,
      isRequired,
      hasError,
      selectedValue,
      handleChange,
      emitValidationState,
    ],
  );

  const scrollButtonVariant = useVisualRefreshEnabled()
    ? 'default'
    : 'emphasized';

  const color = hasError && !isDisabledOrAriaDisabled ? 'critical' : 'neutral';

  const { width: containerWidth } = useContainerSize(containerRef);

  const updateOverflow = useCallback(() => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, offsetWidth } = containerRef.current;
      const tolerance = 4; // px
      setIsOverflowingLeft(scrollLeft > tolerance);
      setIsOverflowingRight(scrollLeft + offsetWidth < scrollWidth - tolerance);
    }
  }, []);

  const slideTabs = (modifier: 1 | -1): void => {
    const container = containerRef.current;
    if (container) {
      container.scrollBy({
        left: (modifier * container.offsetWidth) / 2,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    updateOverflow();
  }, [containerWidth, updateOverflow]);

  return (
    <div
      className={clsx(
        styles.container,
        {
          [styles.disabledGroup]: isDisabledOrAriaDisabled,
          [styles.error]: hasError,
          [styles.readOnly]: isReadOnly,
          [styles.containerWidths.content]: width === 'content',
          [styles.containerWidths.full]: width === 'full',
        },
        consumerClassName,
      )}
      style={{ maxWidth: customWidth, ...consumerStyle }}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-orientation="horizontal"
      role="radiogroup"
      aria-live="polite"
      aria-disabled={ariaDisabled}
      id={formControlId}
      ref={wrapperRef}
      {...remainingProps}
      data-dt-component={COMPONENT_NAME}
    >
      {isOverflowingLeft && (
        <Button
          className={styles.scrollButton}
          style={{ left: '4px', position: 'absolute', top: '4px' }}
          variant={scrollButtonVariant}
          color={color}
          onClick={() => slideTabs(-1)}
          size="condensed"
        >
          <Button.Prefix>
            <ChevronLeftIcon />
          </Button.Prefix>
        </Button>
      )}
      <Flex
        ref={containerRef}
        gap={0}
        onScroll={updateOverflow}
        className={clsx(styles.scrollContainer, {
          [styles.scrollVariants.left]:
            isOverflowingLeft && !isOverflowingRight,
          [styles.scrollVariants.right]:
            !isOverflowingLeft && isOverflowingRight,
          [styles.scrollVariants.both]: isOverflowingLeft && isOverflowingRight,
        })}
      >
        <ToggleButtonGroupContext.Provider value={valueProps}>
          {slots.items?.map((slot, index) => {
            const items = slots.items ?? [];
            const value = slot.props.value;

            const isSelected = value === selectedValue;
            const isLast = index === items.length - 1;
            const nextItem = items[index + 1];
            const nextItemValue = nextItem?.props?.value;

            const nextIsSelected = nextItemValue === selectedValue;
            return (
              <Fragment key={slot.props.value ?? slot.props.id ?? index}>
                {slot}
                {!isLast && (
                  <Divider
                    orientation="vertical"
                    className={clsx(styles.divider, {
                      [styles.hidden]: isSelected || nextIsSelected,
                    })}
                  />
                )}
              </Fragment>
            );
          })}
        </ToggleButtonGroupContext.Provider>
      </Flex>
      {isOverflowingRight && (
        <Button
          className={styles.scrollButton}
          style={{ right: '4px', position: 'absolute', top: '4px ' }}
          variant={scrollButtonVariant}
          color={color}
          onClick={() => slideTabs(1)}
          size="condensed"
        >
          <Button.Suffix>
            <ChevronRightIcon />
          </Button.Suffix>
        </Button>
      )}
    </div>
  );
});

/**
 * `ToggleButtonGroup` lets users toggle between a set of related options, typically with one preselected. It's best for switching between views or a compact group of options with immediate effect.
 * @public
 */
export const ToggleButtonGroup = Object.assign(ToggleButtonGroupRoot, {
  Item,
  Suffix,
  Prefix,
});
